/**
 * ReferToIPDDialog — modal that doctors use to refer a patient from OPD to IPD.
 * Designed to be reused from PatientDetailPage, AppointmentsPage row actions, EMR consult.
 *
 * Usage:
 *   <ReferToIPDDialog open={open} onOpenChange={setOpen} patient={patient} onCreated={r => ...} />
 */
import { useState, useEffect } from 'react';
import { ipdReferralsAPI } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, ArrowRightCircle, Bed } from 'lucide-react';
import { toast } from 'sonner';

const WARD_TYPES = [
  { v: 'general',       label: 'General Ward' },
  { v: 'semi_private',  label: 'Semi-Private' },
  { v: 'private',       label: 'Private' },
  { v: 'icu',           label: 'ICU' },
  { v: 'isolation',     label: 'Isolation' },
];

const ReferToIPDDialog = ({ open, onOpenChange, patient, sourceAppointmentId, sourceVisitId, diagnosisId, defaultDiagnosis, onCreated }) => {
  const [diagnosisSummary, setDiagnosisSummary] = useState('');
  const [wardType, setWardType] = useState('general');
  const [admissionType, setAdmissionType] = useState('inpatient');
  const [urgency, setUrgency] = useState('routine');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDiagnosisSummary(defaultDiagnosis || '');
      setWardType('general'); setAdmissionType('inpatient'); setUrgency('routine');
      setReason(''); setNotes('');
    }
  }, [open, defaultDiagnosis]);

  const submit = async () => {
    if (!patient?.id) return toast.error('Patient is required');
    if (!reason.trim()) return toast.error('Clinical reason is required');
    setSubmitting(true);
    try {
      const res = await ipdReferralsAPI.create({
        patient_id: patient.id,
        source_visit_id: sourceVisitId || null,
        source_appointment_id: sourceAppointmentId || null,
        diagnosis_id: diagnosisId || null,
        diagnosis_summary: diagnosisSummary.trim(),
        suggested_ward_type: wardType,
        suggested_admission_type: admissionType,
        urgency,
        clinical_reason: reason.trim(),
        notes: notes.trim(),
      });
      toast.success('Referral sent — IPD desk will action it.');
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create referral');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="refer-to-ipd-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center text-white"><ArrowRightCircle size={16} /></div>
            Refer Patient for IPD Admission
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            {patient?.first_name} {patient?.last_name} {patient?.uhid && <span className="font-mono">· {patient.uhid}</span>}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 mt-1">
          <div className="col-span-2">
            <Label>Clinical Reason for Admission *</Label>
            <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why does this patient need IPD?" data-testid="refer-reason" />
          </div>
          <div className="col-span-2">
            <Label>Working / Provisional Diagnosis</Label>
            <Input value={diagnosisSummary} onChange={e => setDiagnosisSummary(e.target.value)} data-testid="refer-diagnosis" />
          </div>
          <div>
            <Label>Admission Type</Label>
            <Select value={admissionType} onValueChange={setAdmissionType}>
              <SelectTrigger data-testid="refer-admtype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inpatient">Inpatient</SelectItem>
                <SelectItem value="day_care"><span className="inline-flex items-center gap-1"><Bed size={12} /> Day Care</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Suggested Ward</Label>
            <Select value={wardType} onValueChange={setWardType}>
              <SelectTrigger data-testid="refer-wardtype"><SelectValue /></SelectTrigger>
              <SelectContent>{WARD_TYPES.map(w => <SelectItem key={w.v} value={w.v}>{w.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Urgency</Label>
            <div className="flex gap-2 mt-1">
              {['routine', 'urgent', 'emergency'].map(u => (
                <button key={u} type="button"
                  onClick={() => setUrgency(u)}
                  className={`flex-1 py-2 px-3 text-xs font-semibold rounded-lg border transition ${
                    urgency === u
                      ? (u === 'emergency' ? 'bg-rose-500 text-white border-rose-500' : u === 'urgent' ? 'bg-amber-500 text-white border-amber-500' : 'bg-slate-700 text-white border-slate-700')
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`refer-urgency-${u}`}>
                  {u === 'emergency' && <AlertTriangle size={11} className="inline mr-1" />} {u.charAt(0).toUpperCase() + u.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="col-span-2">
            <Label>Notes for IPD team (optional)</Label>
            <Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Preferred timing, accompanying family, allergies, special needs..." data-testid="refer-notes" />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-gradient-to-br from-sky-500 to-indigo-600 hover:opacity-90 text-white" data-testid="refer-submit">
            <ArrowRightCircle size={14} className="mr-1.5" /> {submitting ? 'Sending…' : 'Send to IPD'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReferToIPDDialog;
